/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/featureCodesApi.ts
 🗓️ Created: 2025-01-22
 🧩 Description:
    API calls for Feature Codes (fetch list, fetch by ID, update).
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { FeatureCode, FeatureCodeModification } from '../types';

export interface FetchFeatureCodesParams {
  categoryId?: number;
  search?: string;
  domainId?: number;
  domainName?: string;
}

export const fetchFeatureCodes = async (
  params: FetchFeatureCodesParams = {},
  tenantSlug?: string,
  userId?: string
): Promise<FeatureCode[]> => {
  const queryParams = new URLSearchParams();
  if (params.categoryId) {
    queryParams.append('categoryId', params.categoryId.toString());
  }
  if (params.search) {
    queryParams.append('search', params.search);
  }
  if (params.domainId && !isNaN(params.domainId)) {
    queryParams.append('domainId', params.domainId.toString());
  }
  if (params.domainName) {
    queryParams.append('domainName', params.domainName);
  }

  const url = buildApiUrl(`/api/telephony/feature-codes${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
  const headers = getApiHeaders(tenantSlug, userId);

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error('Failed to fetch feature codes');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch feature codes');
  }

  return Array.isArray(result.data) ? result.data : [];
};

export const fetchFeatureCodeById = async (
  id: string,
  tenantSlug?: string,
  userId?: string
): Promise<FeatureCode> => {
  const url = buildApiUrl(`/api/telephony/feature-codes/${id}`);
  const headers = getApiHeaders(tenantSlug, userId);

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error('Failed to fetch feature code');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch feature code');
  }

  return result.data;
};

export const updateFeatureCode = async (
  id: string,
  payload: FeatureCodeModification,
  tenantSlug?: string,
  userId?: string
): Promise<FeatureCode> => {
  const url = buildApiUrl(`/api/telephony/feature-codes/${id}`);
  const headers = getApiHeaders(tenantSlug, userId);

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to update feature code');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to update feature code');
  }

  return result.data;
};

